/**
 * Suivi optionnel et léger des Core Web Vitals (LCP, CLS, INP) via les
 * PerformanceObserver natifs, sans dépendance externe. Les valeurs sont
 * journalisées via LogUtils ; on peut brancher un endpoint d'analytics interne
 * (respectant la CSP connect-src 'self') en remplaçant `report`.
 */

import { LogUtils } from './Librairies/utils/logUtils.js';

type MetricName = 'LCP' | 'CLS' | 'INP';

function report(name: MetricName, value: number): void {
  LogUtils.Log(`[web-vitals] ${name}: ${Math.round(value * 100) / 100}`, 'info');
}

function observeLcp(): void {
  let lcp = 0;
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      lcp = entry.startTime;
    }
  });
  observer.observe({ type: 'largest-contentful-paint', buffered: true });

  addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden' && lcp > 0) {
        report('LCP', lcp);
        observer.disconnect();
      }
    },
    { once: true }
  );
}

function observeCls(): void {
  let cls = 0;
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const layoutShift = entry as PerformanceEntry & { value: number; hadRecentInput: boolean };
      if (!layoutShift.hadRecentInput) {
        cls += layoutShift.value;
      }
    }
  });
  observer.observe({ type: 'layout-shift', buffered: true });

  addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') {
        report('CLS', cls);
        observer.disconnect();
      }
    },
    { once: true }
  );
}

function observeInp(): void {
  let worst = 0;
  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      const event = entry as PerformanceEntry & { duration: number };
      worst = Math.max(worst, event.duration);
    }
  });
  try {
    observer.observe({ type: 'event', buffered: true, durationThreshold: 40 } as PerformanceObserverInit);
  } catch {
    return;
  }

  addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden' && worst > 0) {
        report('INP', worst);
        observer.disconnect();
      }
    },
    { once: true }
  );
}

export function initWebVitals(): void {
  if (!('PerformanceObserver' in window)) {
    return;
  }
  try {
    observeLcp();
    observeCls();
    observeInp();
  } catch {
    // Suivi best-effort : on ignore silencieusement les navigateurs incompatibles.
  }
}
